import { Constants } from "./constants";

/**
 * Title tracker registry — the single list of sports the Ribbon Room tracks.
 *
 * Shared by the API (visibility settings typing), the web Dog Profile and the
 * mobile Dog Profile so a tracker is defined exactly once.
 */
export interface ITitleTrackerDefinition {
  id: TitleTrackerId;
  label: string;
  description: string;
  show_type: string;
  sanctioning_club: string;
}

export const TITLE_TRACKER_IDS = [
  "akc_fast_cat",
  "akc_rally",
  "akc_obedience",
  "akc_scent_work",
  "akc_fetch",
  "ukc_nosework",
  "ukc_rally",
  "ukc_obedience",
  "bha_barn_hunt",
] as const;

export type TitleTrackerId = (typeof TITLE_TRACKER_IDS)[number];

/** Per-tracker visibility flags stored on the dog (`tracker_visibility_settings`). */
export type TitleTrackerVisibilitySettings = Partial<Record<TitleTrackerId, boolean>>;

export const TITLE_TRACKERS: Record<TitleTrackerId, ITitleTrackerDefinition> = {
  akc_fast_cat: {
    id: "akc_fast_cat",
    label: "AKC Fast CAT",
    description: "Coursing Ability Test",
    show_type: Constants.show_type.fast_cat,
    sanctioning_club: Constants.sanctioning_club.AKC,
  },
  akc_rally: {
    id: "akc_rally",
    label: "AKC Rally",
    description: "Rally Obedience",
    show_type: Constants.show_type.rally_obedience,
    sanctioning_club: Constants.sanctioning_club.AKC,
  },
  akc_obedience: {
    id: "akc_obedience",
    label: "AKC Obedience",
    description: "Obedience",
    show_type: Constants.show_type.obedience,
    sanctioning_club: Constants.sanctioning_club.AKC,
  },
  akc_scent_work: {
    id: "akc_scent_work",
    label: "AKC Scent Work",
    description: "Scent Work",
    show_type: Constants.show_type.scentwork,
    sanctioning_club: Constants.sanctioning_club.AKC,
  },
  akc_fetch: {
    id: "akc_fetch",
    label: "AKC Fetch",
    description: "Fetch",
    show_type: Constants.show_type.fetch,
    sanctioning_club: Constants.sanctioning_club.AKC,
  },
  ukc_nosework: {
    id: "ukc_nosework",
    label: "UKC Nosework",
    description: "Detection & Scent Work",
    show_type: Constants.show_type.nosework,
    sanctioning_club: Constants.sanctioning_club.UKC,
  },
  ukc_rally: {
    id: "ukc_rally",
    label: "UKC Rally",
    description: "Rally Obedience",
    show_type: Constants.show_type.ukc_rally,
    sanctioning_club: Constants.sanctioning_club.UKC,
  },
  ukc_obedience: {
    id: "ukc_obedience",
    label: "UKC Obedience",
    description: "Obedience",
    show_type: Constants.show_type.ukc_obedience,
    sanctioning_club: Constants.sanctioning_club.UKC,
  },
  bha_barn_hunt: {
    id: "bha_barn_hunt",
    label: "BHA Barn Hunt",
    description: "Barn Hunt Association",
    show_type: Constants.show_type.barn_hunt,
    sanctioning_club: Constants.sanctioning_club.BHA,
  },
};

/** Ordered list of tracker definitions (display order = registry order). */
export const TITLE_TRACKER_LIST: ITitleTrackerDefinition[] = TITLE_TRACKER_IDS.map(
  (id) => TITLE_TRACKERS[id]
);

/** Sanctioning-club filter chips shown above the Ribbon Room cards. */
export const TITLE_TRACKER_CLUB_FILTERS = ["All", "AKC", "UKC", "BHA"] as const;
export type TitleTrackerClubFilter = (typeof TITLE_TRACKER_CLUB_FILTERS)[number];

/**
 * Resolve the tracker id for a sport payload (sanctioning club + show type).
 * Returns null when the sport is not tracked.
 */
export const getTitleTrackerId = (
  sanctioningClub: string | undefined,
  showType: string | undefined
): TitleTrackerId | null => {
  if (!sanctioningClub || !showType) return null;
  const match = TITLE_TRACKER_LIST.find(
    (t) => t.sanctioning_club === sanctioningClub && t.show_type === showType
  );
  return match ? match.id : null;
};

/**
 * Visibility rule used by both clients: missing settings / missing key = visible.
 */
export const isTitleTrackerVisible = (
  trackerId: TitleTrackerId | null,
  settings: TitleTrackerVisibilitySettings | undefined | null
): boolean => {
  if (!trackerId) return true;
  if (!settings) return true;
  const value = settings[trackerId];
  return value === undefined ? true : value;
};
